<template>
	<Teleport v-if="showDesktopPreview" to="#measurements-selector-desktop-preview">
		<ShapePreview
			v-if="showDesktopPreview"
			v-bind:primitives="primitives"
			v-bind:unit="unit"
			:onResize="resizeProductGallery"
		></ShapePreview>
	</Teleport>
	<Teleport v-if="initialized && showDesktopPreview" to="#measurements-selector-desktop-controls">
		<PreviewButtongroup v-if="previewIsShown" />
	</Teleport>
	<portal
		v-else-if="!showDesktopPreview && shape === 'dxf'"
		to="shape-preview"
		tag="div"
	>
		<ShapePreview
			v-bind:primitives="primitives"
			v-bind:unit="unit"
			:canPreview="canPreview"
		></ShapePreview>
	</portal>
	<template v-else-if="!showDesktopPreview && shape !== ''">
		<ShapePreview
			v-bind:primitives="primitives"
			v-bind:unit="unit"
			:canPreview="canPreview"
		></ShapePreview>
	</template>
</template>

<script>
import ShapePreview from "./ShapePreview";
import debounce from "lodash/debounce";
import { mapGetters, mapMutations } from "vuex";
import PreviewButtongroup from "@/components/PreviewButtongroup";

export default {
	props: {
		primitives: Object,
		unit: String,
	},
	mounted: function() {
		this.setProductGallery();
		this.addFullScreenListener();
	},
	created: function() {
		window.addEventListener( 'resize', this.onResize );
	},
	beforeUnmount: function() {
		window.removeEventListener( 'resize', this.onResize );
	},
	data: function() {
		return {
			initialized: false,
			galleryItemId: "#measurements-selector-desktop-preview",
			controlsItemId: "measurements-selector-desktop-controls",
			productGallery: null,
			showDesktopPreview: this.isMediaDesktopSize(),
			previewIsShown: false,
		};
	},
	watch: {

		// If the user interacts with the measurements configurator
		// we need to show the preview.
		usesDefaultDimensions: function( newValue, oldValue ) {
			if( newValue !== oldValue ) {
				this.resizeProductGallery({ canPreview: this.canPreview });
			}
		},
		isShapeComplete: function( newValue, oldValue ) {
			if( newValue !== oldValue ) {
				this.resizeProductGallery({ canPreview: this.canPreview });
			}
		},
		sideUserSelected: function( newValue, oldValue ) {
			if( newValue !== oldValue ) {
				this.resizeProductGallery({ canPreview: this.canPreview });
			}
		},
	},
	computed: {
		i18n: function() { return this.$i18n; },
		...mapGetters( [
			'rotation',
			'usesDefaultDimensions',
			'shape',
			'isShapeComplete',
			'sideUserSelected'
		] )
	},
	methods: {
		setProductGallery: function() {

			// Only set the reference if we haven't before.
			if ( this.productGallery !== null ) {
				return;
			}

			const $ = jQuery;

			// This finds the gallery in which our measurements preview is contained.
			const $wrap      = $( document.querySelector( '.kps-gallery' ) );
			const $gallery   = $( document.getElementById( 'js-kps-gallery' ) );
			const $preview   = $( document.querySelector('[data-product-slider-preview-container]') );
			const keenSlider = $gallery[ 0 ]?.keenSlider;

			// This means flexslider hasn't been initialized yet.
			if ( ! keenSlider ) {

				// WooCommerce triggers this event when it is done initializing the gallery.
				$gallery.on( 'wc-product-gallery-after-init', this.setProductGallery );
				return;
			}

			this.init( {
				$wrap,
				$gallery,
				$preview,
				keenSlider,
			} );
		},
		resizeProductGallery: function( ) {
			if ( ! this.initialized ) {
				return;
			}

			const $ = jQuery;
			const { keenSlider, $preview, $previewButton } = this.productGallery;
			if (
				this.isShapeComplete
				&& this.showDesktopPreview
				&& ! this.usesDefaultDimensions
				&& this.shape.length > 0
				|| this.isShapeComplete
				&& this.showDesktopPreview
				&& this.usesDefaultDimensions
				&& this.shape.length > 0
				&& this.sideUserSelected
				|| this.shape === 'dxf'
				&& this.showDesktopPreview
			) {
				this.showPreview();
			} else {
				if ( this.previewIsShown ) {
					this.hidePreview();
					keenSlider.moveToSlide( 0 );
				}
			}
		},
		init: function( productGallery ) {
			const $ = jQuery;

			// Our WordPress filter is very late so our preview item should always be the last one.
			const $previewButton = $( document.querySelector( '[data-gallery-navigation-preview-trigger]' ) );

			// Save relevant elements for later reference
			productGallery.$previewButton = $previewButton;

			this.productGallery = productGallery;
			this.addPreviewButtongroup();
			this.initialized = true;
		},
		showPreview() {
			this.productGallery.$wrap.addClass('js-show-preview');
			this.previewIsShown = true;
		},
		hidePreview() {
			this.productGallery.$wrap.removeClass('js-show-preview');
			this.previewIsShown = false;
		},
		addPreviewButtongroup() {
			const { $gallery } = this.productGallery;

			$gallery.prepend( '<div class="measurements-selector__preview-controls" id="' + this.controlsItemId + '"></div>' );
		},
		isMediaDesktopSize: function() {
			const media = window.matchMedia( "(min-width: 992px)" );

			return media.matches;
		},
		onResize: debounce( function() {
			const previousShowDesktopPreview = this.showDesktopPreview;

			this.showDesktopPreview = this.isMediaDesktopSize();

			if ( previousShowDesktopPreview !== this.showDesktopPreview ) {
				this.resizeProductGallery();
			}

			/*
			 * Because resizing a window doesn't happen all the time, use a relatively long debounce. Having no slowdown
			 * is more important than a constant up-to-date view.
			 */
		}, 200 ),
		addFullScreenListener: function() {
			document.querySelector( '.js-product-zoom' )?.addEventListener('click', () =>{
				this.updateZoom(1);
				this.updateRotation(0);
			});
		},
		...mapMutations( [
			'updateZoom',
			'updateRotation',
			'updateTranslation'
		] )
	},
	components: {
		PreviewButtongroup,
		ShapePreview
	}
};
</script>
